import { useRef, useState, useEffect } from "react";
import { Dialog, Heading, Button, Text } from "@rshbintech.dboul/core-ui";
import { monolithApi, AuthService } from "@rshbintech.dboul/http";
import { analytics } from "@rshbintech.dboul/analytics";
import { useTranslation } from "@rshbintech.dboul/utils";
import { createMetricDataObject } from "../../common/utils/create-metric-data-object";
import { AppLocale } from "../../common/types";
import { AuthNameSpaceType } from "../types";
import {
  setShowWarningDialogItemToLocalStorage,
  getShowWarningDialogItemFromLocalStorage,
  removeShowWarningDialogItemFromLocalStorage,
} from "./utils";
import {
  AUTO_LOGOUT_METRIC_NAME,
  AUTO_LOGOUT_METRIC_DATA,
  SHOW_WARNING_DIALOG_DURATION,
  DELAY_USER_LOGOUT_PROCESS_EVENTS,
} from "./constants";
import styles from "./styles.scss";

export const AutoLogout = () => {
  const { translate } = useTranslation<AppLocale, AuthNameSpaceType>(
    "AUTO_LOGOUT"
  );

  const [userIsAuthenticated, setUserIsAuthenticated] = useState(false);

  const timeToLogoutUserTimer = useRef<number>();
  const checkUserInactivityTimer = useRef<number>();
  const timeToShowWarningDialogTimer = useRef<number>();

  const [openedWarningDialog, setOpenedWarningDialog] = useState(false);

  const openWarningDialog = () => {
    setOpenedWarningDialog(true);
  };

  const closeWarningDialog = () => {
    setOpenedWarningDialog(false);
  };

  const clearTimersAndRemoveLocalStorageItem = () => {
    clearInterval(checkUserInactivityTimer.current);
    clearTimeout(timeToLogoutUserTimer.current);
    clearTimeout(timeToShowWarningDialogTimer.current);
    removeShowWarningDialogItemFromLocalStorage();
  };

  /* Функция обновляющая время до отображения окна предупреждения */
  const updateTimeToShowWarningDialog = () => {
    if (timeToShowWarningDialogTimer.current) {
      clearTimeout(timeToShowWarningDialogTimer.current);
    }

    timeToShowWarningDialogTimer.current = window.setTimeout(
      setShowWarningDialogItemToLocalStorage,
      200
    );
  };

  const handleLogout = async () => {
    try {
      await monolithApi.auth.jwtLogoutCreate();
    } catch (error) {
      /* TODO: Уточнить действия в случае ошибки логаута */
    }
  };

  const handleCancelLogout = () => {
    clearTimeout(timeToLogoutUserTimer.current);
    updateTimeToShowWarningDialog();
    closeWarningDialog();
  };

  /* Таймер логаута пользователя запускающийся после отображения окна предупреждения */
  const startTimerForLogoutUser = () => {
    if (timeToLogoutUserTimer.current) {
      clearTimeout(timeToLogoutUserTimer.current);
    }

    timeToLogoutUserTimer.current = window.setTimeout(async () => {
      try {
        await analytics.sendEventAndWait(
          AUTO_LOGOUT_METRIC_NAME,
          createMetricDataObject(AUTO_LOGOUT_METRIC_DATA.AUTO_LOGOUT)
        );
      } finally {
        AuthService.setLoginPageNextParam(window.location.pathname);

        handleLogout();
      }
    }, SHOW_WARNING_DIALOG_DURATION);
  };

  /* Таймер проверяющий активность пользователя */
  const startCheckUserInactivity = () => {
    if (checkUserInactivityTimer.current) {
      clearInterval(checkUserInactivityTimer.current);
    }

    checkUserInactivityTimer.current = window.setInterval(() => {
      const currentTime = Date.now();
      const timeToShowWarningDialog = parseInt(
        getShowWarningDialogItemFromLocalStorage() || "0",
        10
      );

      /* Если окно предупреждения не отображено на экране, при этом время его отображения наступило  */
      if (currentTime >= timeToShowWarningDialog && !openedWarningDialog) {
        /* то отображаем окно предупреждения */
        openWarningDialog();
        /* и запускаем таймер логаута пользователя */
        startTimerForLogoutUser();

        analytics.sendEvent(
          AUTO_LOGOUT_METRIC_NAME,
          createMetricDataObject(AUTO_LOGOUT_METRIC_DATA.OPEN_DIALOG)
        );

        /*
         * Если окно предупреждения отображено на экране, при этом время его отображения еще не наступило,
         * то делаем вывод что мы находимся в неактивной вкладке и время отображения окна предупреждения
         * было обновлено закрытием точно такого же окна в соседней активной вкладке.
         */
      } else if (currentTime < timeToShowWarningDialog && openedWarningDialog) {
        /* В результате чего мы делаем все тоже самое (закрываем окно и отменяем процесс логаута), но уже для текущей неактивной вкладки */
        handleCancelLogout();
      }
    }, 1000);
  };

  const handleCloseDialog = () => {
    analytics.sendEvent(
      AUTO_LOGOUT_METRIC_NAME,
      createMetricDataObject(AUTO_LOGOUT_METRIC_DATA.CLOSE_DIALOG)
    );
    handleCancelLogout();
  };

  const handleCancelLogoutButtonClick = () => {
    analytics.sendEvent(
      AUTO_LOGOUT_METRIC_NAME,
      createMetricDataObject(AUTO_LOGOUT_METRIC_DATA.CANCEL_LOGOUT_BUTTON_CLICK)
    );
    handleCancelLogout();
  };

  const handleLogoutButtonClick = async () => {
    try {
      await analytics.sendEventAndWait(
        AUTO_LOGOUT_METRIC_NAME,
        createMetricDataObject(AUTO_LOGOUT_METRIC_DATA.LOGOUT_BUTTON_CLICK)
      );
    } finally {
      handleLogout();
    }
  };

  useEffect(() => {
    AuthService.onUserLogin(() => {
      setUserIsAuthenticated(true);
      setShowWarningDialogItemToLocalStorage();
    });

    AuthService.onUserLogout(() => {
      clearTimersAndRemoveLocalStorageItem();
    });

    return () => {
      clearTimersAndRemoveLocalStorageItem();
    };
  }, []);

  useEffect(() => {
    if (userIsAuthenticated) {
      startCheckUserInactivity();
    }
  }, [userIsAuthenticated, openedWarningDialog]);

  useEffect(() => {
    if (userIsAuthenticated && !openedWarningDialog) {
      DELAY_USER_LOGOUT_PROCESS_EVENTS.forEach((event) => {
        document.addEventListener(
          event.name,
          updateTimeToShowWarningDialog,
          event.capture
        );
      });

      return () => {
        DELAY_USER_LOGOUT_PROCESS_EVENTS.forEach((event) => {
          document.removeEventListener(
            event.name,
            updateTimeToShowWarningDialog,
            event.capture
          );
        });
      };
    }
  }, [userIsAuthenticated, openedWarningDialog]);

  return (
    <Dialog
      className={styles.dialog}
      opened={openedWarningDialog}
      onClose={handleCloseDialog}
    >
      <>
        <Heading className={styles.title} level={4}>
          {translate("DIALOG_HEADING_TEXT")}
        </Heading>
        <Text className={styles.description}>
          {translate("DIALOG_DESCRIPTION_TEXT")}
        </Text>
        <div className={styles["buttons-container"]}>
          <Button type="secondary" onClick={handleLogoutButtonClick}>
            {translate("DIALOG_LOGOUT_BUTTON_TEXT")}
          </Button>
          <Button onClick={handleCancelLogoutButtonClick}>
            {translate("DIALOG_CANCEL_LOGOUT_BUTTON_TEXT")}
          </Button>
        </div>
      </>
    </Dialog>
  );
};
